import { Flex, List, Pill, Text, ToggleDetails } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import RichTextEditor from "react-rte";
import { ReflectionService } from "../../services/ReflectionService";
import { formatToReadableTime } from "../../utils/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";

export const ReflectionVersionsToggle = ({
  contextId,
  reflection,
  currentReflectionIsDraft,
  actions,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [versions, setVersions] = useState([]);
  const [loadVersions, setLoadVersions] = useState(false);

  async function handleToggle() {
    try {
      setLoadVersions(true);
      if (reflection.from_template) {
        const promiseList = reflection.sections.map((section) =>
          ReflectionService.getReflectionVersionHistory(
            contextId,
            reflection.ddl_id,
            section.id
          )
        );
        let versions = await Promise.all(promiseList);

        versions.forEach((section, i) => {
          section.forEach((version) => {
            version.section_id = reflection.sections[i].id;
          });
        });
        versions = versions.flat();
        const versionsByDate = Object.groupBy(versions, (v) => v.last_modified);
        const versionsList = Object.values(versionsByDate).sort(
          (a, b) => new Date(b[0].last_modified) - new Date(a[0].last_modified)
        );

        if (currentReflectionIsDraft) {
          setVersions(versionsList);
        } else {
          versionsList.shift();
          setVersions(versionsList);
        }
      } else {
        const versions = await ReflectionService.getReflectionVersionHistory(
          contextId,
          reflection.ddl_id
        );
        if (versions) {
          if (currentReflectionIsDraft) {
            setVersions(versions);
          } else {
            setVersions(versions.filter((v) => !v.is_latest));
          }
        }
      }
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("reflections.reflectionVersions"),
        }),
        ERROR
      );
    } finally {
      setLoadVersions(false);
    }
  }

  return (
    <ToggleDetails
      summary={<Text>{t("reflections.reflectionVersions")}</Text>}
      onToggle={(_e, expanded) => {
        if (expanded) handleToggle();
      }}
    >
      {loadVersions ? (
        <Skeleton
          count={3}
          style={{
            marginBottom: 10,
            marginLeft: "1.5rem",
            width: "stretch",
            height: 98,
          }}
        />
      ) : versions.length === 0 ? (
        <Text>{t("reflections.noVersions")}</Text>
      ) : (
        versions.map((version, key) => {
          const details = reflection.from_template ? version[0] : version;
          return (
            <Card
              key={key}
              style={{
                border: "1px solid #ccc",
                marginBottom: "10px",
                padding: "10px",
                marginLeft: "1.5rem",
              }}
            >
              <Flex gap="small" wrap="wrap" alignItems="start">
                <Flex.Item shouldGrow>
                  <Text as="p" size="small">
                    {details.author} <br />
                    {formatToReadableTime(new Date(details.last_modified))}
                  </Text>
                </Flex.Item>
                <Flex.Item>
                  {details.is_latest && (
                    <Flex.Item>
                      <Pill color="success">{t("reflections.current")}</Pill>
                    </Flex.Item>
                  )}
                </Flex.Item>
                <Flex.Item size="100%">
                  {reflection.from_template ? (
                    <Flex wrap="wrap" gap="medium">
                      {reflection.sections.map((section, i) => {
                        const sectionContent = version.find(
                          (v) => v.section_id === section.id
                        ).content;
                        return (
                          <Flex.Item key={i} width="100%">
                            <Flex wrap="wrap" gap="small">
                              <Flex.Item width="100%">
                                <Text weight="bold">
                                  {section.header_question}
                                </Text>
                              </Flex.Item>
                              {section.helper_text &&
                                section.helper_text !== EMPTY_RTE && (
                                  <Flex.Item>
                                    <RichTextEditor
                                      className="readOnlyClass"
                                      value={RichTextEditor.createValueFromString(
                                        section.helper_text,
                                        "html"
                                      )}
                                      readOnly
                                    />
                                  </Flex.Item>
                                )}
                              <Flex.Item padding="0 0 0 large">
                                <div
                                  style={{
                                    borderLeft: "2px solid #03893D",
                                    paddingLeft: "0.75rem",
                                  }}
                                >
                                  <RichTextEditor
                                    className="readOnlyClass"
                                    value={RichTextEditor.createValueFromString(
                                      sectionContent,
                                      "html"
                                    )}
                                    readOnly
                                  />
                                </div>
                              </Flex.Item>
                            </Flex>
                          </Flex.Item>
                        );
                      })}
                    </Flex>
                  ) : (
                    <RichTextEditor
                      className="readOnlyClass"
                      readOnly
                      value={RichTextEditor.createValueFromString(
                        details.content,
                        "html"
                      )}
                    />
                  )}
                </Flex.Item>
                {actions && (
                  <Flex.Item size="100%">
                    <Text weight="bold" as="div">
                      {t("actions.actions")}
                    </Text>
                    {actions.some((action) =>
                      details.actions.includes(action.id)
                    ) ? (
                      <List>
                        {details.actions.map((actionId, key) => {
                          const action = actions.find(
                            (action) => action.id === actionId
                          );
                          if (action) {
                            return (
                              <List.Item key={key}>
                                <Text as="p" size="small">
                                  {action.text}
                                </Text>
                              </List.Item>
                            );
                          }
                        })}
                      </List>
                    ) : (
                      <Text as="p" size="small">
                        {t("actions.noActions")}
                      </Text>
                    )}
                  </Flex.Item>
                )}
              </Flex>
            </Card>
          );
        })
      )}
    </ToggleDetails>
  );
};
